import { AvatarComposition } from '@codeleap/mobile'
import { createStyles } from '@codeleap/styles'
import { StyleRegistry } from '../styles'
import { textVariant } from './Text'

export type AvatarInfoStates = 'highlighted'

type AvatarParts =
  | `avatar${Capitalize<AvatarComposition>}`

export type AvatarInfoParts =
  | 'wrapper'
  | 'contentWrapper'
  | 'titleWrapper'
  | 'title'
  | 'titleIcon'
  | 'descriptionWrapper'
  | 'description'
  | 'descriptionIcon'
  | 'descriptionRow'

export type AvatarInfoComposition = AvatarInfoParts | AvatarParts | `${AvatarInfoParts}:${AvatarInfoStates}`

const createAvatarInfoVariant = createStyles<AvatarInfoComposition>

export const AvatarInfoStyles = {
  default: createAvatarInfoVariant((theme) => {
    const avatarSize = theme.size.large

    return {
      wrapper: {
        ...theme.presets.row,
        ...theme.presets.alignCenter,
        ...theme.spacing.gap(1.5),
      },
      avatarWrapper: {
        width: avatarSize,
        height: avatarSize,
        minWidth: avatarSize,
        maxWidth: avatarSize,
        minHeight: avatarSize,
        maxHeight: avatarSize,
      },
      avatarImage: {
        height: avatarSize,
        width: avatarSize,
      },
      contentWrapper: {
        ...theme.presets.flex,
        ...theme.presets.column,
        ...theme.spacing.gap(0.25),
      },
      titleWrapper: {
        ...theme.presets.row,
        ...theme.spacing.gap(0.75),
        ...theme.presets.alignCenter,
      },
      title: {
        ...textVariant('p2'),
        color: theme.colors.textHeadingHighlighted,
      },
      titleIcon: {
        width: theme.values.iconSize[1],
        height: theme.values.iconSize[1],
        color: theme.colors.buttonHighlightPrimaryBgDefault,
      },
      descriptionWrapper: {
        ...theme.presets.row,
      },
      descriptionRow: {},
      description: {
        ...textVariant('p3'),
        color: theme.colors.textCaption,

        // Ellipsis stuff
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        wordBreak: 'break-word',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
      },
      'description:highlighted': {
        ...textVariant('p3'),
        color: theme.baseColors.neutralBlackTransparent700,
      },
      descriptionIcon: {
        width: theme.values.iconSize[1],
        height: theme.values.iconSize[1],
        color: theme.baseColors.neutralBlackTransparent700,
        paddingTop: theme.value(0.5),
      },
    }
  }),
  mid: createAvatarInfoVariant((theme) => {
    const avatarSize = theme.size.mediumish
    return {
      wrapper: {
        ...theme.spacing.gap(1),
      },
      avatarWrapper: {
        width: avatarSize,
        height: avatarSize,
        minWidth: avatarSize,
        maxWidth: avatarSize,
        minHeight: avatarSize,
        maxHeight: avatarSize,
      },
      avatarImage: {
        height: avatarSize,
        width: avatarSize,
      },
      avatarInitials: {
        ...textVariant(['semiBold', 'p4']),
        color: theme.baseColors.primarySolid50,
      },
      contentWrapper: {
        ...theme.spacing.gap(0.25),
      },
      title: {
        ...textVariant(['p3', 'medium']),
        color: theme.colors.textHeadingHighlighted,
      },
      description: {
        ...textVariant('c2'),
        color: theme.baseColors.neutralBlackTransparent700,
      },
    }
  }),
  small: createAvatarInfoVariant((theme) => {
    const avatarSize = theme.size.medium
    return {
      wrapper: {
        ...theme.spacing.gap(1),
      },
      avatarWrapper: {
        width: avatarSize,
        height: avatarSize,
      },
      avatarImage: {
        height: avatarSize,
        width: avatarSize,
      },
      contentWrapper: {
        ...theme.spacing.gap(0),
      },
      title: {
        ...textVariant('p3'),
        color: theme.colors.textHeadingHighlighted,
      },
      description: {
        ...textVariant('c2'),
        color: theme.baseColors.neutralBlackTransparent700,
      },
    }
  }),
  'x-large': createAvatarInfoVariant((theme) => {
    const avatarSize = theme.size['x-large']
    return {
      wrapper: {
        ...theme.spacing.gap(1),
      },
      avatarWrapper: {
        width: avatarSize,
        height: avatarSize,
        minWidth: avatarSize,
        maxWidth: avatarSize,
        minHeight: avatarSize,
        maxHeight: avatarSize,
      },
      avatarImage: {
        height: avatarSize,
        width: avatarSize,
      },
      contentWrapper: {
        ...theme.spacing.gap(0),
      },
      title: {
        ...textVariant('p2'),
        fontSize: 18,
        color: theme.colors.textHeadingHighlighted,
      },
      description: {
        ...textVariant('p3'),
        fontSize: 16,
        color: theme.baseColors.neutralBlackTransparent700,
      },
    }
  }),
  'highlighted:missed': createAvatarInfoVariant(theme => ({
    title: {
      color: theme.baseColors.redSolid500,
      fontWeight: 600,
    },
  })),
  pageHeader: createAvatarInfoVariant((theme) => ({
    title: {
      color: theme.colors.buttonHighlightPrimaryLabelDefault,
      fontWeight: 700,
    },
  })),
  chatPreview: createAvatarInfoVariant((theme) => ({
    wrapper: {
      flex: 1,
      minWidth: 0,
    },
    avatarWrapper: {
      ...theme.presets.alignSelfStart,
      flexShrink: 0,
    },
    contentWrapper: {
      minWidth: 0,
      flex: 1,
    },
    titleWrapper: {
      overflow: 'hidden',
      maxWidth: '100%',
      minWidth: 0,
    },
    title: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: '100%',
      display: 'block',
    },
    descriptionIcon: {
      width: theme.values.iconSize[1.5],
      height: theme.values.iconSize[1.5],
      color: theme.baseColors.neutralSolid400,
      flexShrink: 0,
    },
    description: {
      ...textVariant('p3'),
      color: theme.colors.textParagraph,
      flex: 1,
      flewGrow: 1,
      maxWidth: '100%',
      width: '100%',
      fontWeight: 400,
    },
  })),
}

StyleRegistry.registerVariants('AvatarInfo', AvatarInfoStyles)
